import { useNavigate } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { bookmarkStep, type BranchPath } from '@/api/onboarding'
import { useShowcaseAccess } from '@/showcase/access'
import { SHOWCASE_ENABLED } from '@/config/showcase'

type Destination = 'yandex' | 'tariff' | 'dashboard' | 'showcase-consent'

interface BranchOption {
  branch: BranchPath
  destination: Destination
  name: string
  desc: string
  time: string
  featured?: boolean
  minor?: boolean
  // Pill справа от названия. Используется для маркировки тарифной модели
  // карточки (например «по подписке Профи» для Витрины).
  badge?: string
  icon: JSX.Element
}

const OPTIONS: BranchOption[] = [
  {
    branch: 'ads',
    destination: 'yandex',
    name: 'Запустить рекламу',
    desc: 'Подключим Я.Директ, соберём креативы и первую кампанию — настройка под ключ. ≈ 6 минут.',
    time: '4 шага · ≈ 6 мин',
    featured: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
  {
    branch: 'quick',
    destination: 'dashboard',
    name: 'Создать лендинг',
    desc: 'Короткая ссылка tgryx.ru/slug → канал. Базовая или с SEO-страницей. Настроите в кабинете.',
    time: 'сразу в кабинет',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
    ),
  },
  {
    branch: 'analytics',
    destination: 'dashboard',
    name: 'Умную кнопку',
    desc: 'Inline-кнопка к посту: подписка, переход в бот, оплата. Создадите в кабинете — это бесплатная функция.',
    time: 'сразу в кабинет',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
        <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
      </svg>
    ),
  },
  {
    branch: 'analytics',
    destination: 'dashboard',
    name: 'Включить комментарии',
    desc: 'Антиспам, авто-ответы и модерация в связанной группе. Бесплатно, настройка пары минут.',
    time: 'сразу в кабинет',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    branch: 'analytics',
    destination: 'dashboard',
    name: 'Просто аналитика',
    desc: 'Хочу пока понаблюдать — охваты, прирост, источники. Без рекламы и кнопок.',
    time: 'сразу в кабинет',
    minor: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
      </svg>
    ),
  },
]

export function BranchPage() {
  const navigate = useNavigate()
  const showcaseAccess = useShowcaseAccess()

  // Витрина (Phase 1) — карточка видна только whitelist'у. Branch=showcase
  // ведёт в новый под-флоу /onboarding/showcase-consent → /showcase-rules →
  // /onboarding/tariff. После расширения whitelist'а — снимаем гейт.
  const options: BranchOption[] = SHOWCASE_ENABLED && showcaseAccess
    ? [
        OPTIONS[0],
        {
          branch: 'showcase' as BranchPath,
          destination: 'showcase-consent',
          name: 'Витрина в поиске',
          desc: 'Публикуем посты на нашем портале — их находят в Google и Яндексе и переходят в канал. Бесплатные читатели из поиска.',
          time: '2 шага · согласие',
          featured: true,
          badge: 'по подписке Профи',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          ),
        },
        ...OPTIONS.slice(1),
      ]
    : OPTIONS

  function handlePick(opt: BranchOption) {
    if (opt.destination === 'yandex') {
      bookmarkStep('yandex-connect', opt.branch)
      navigate('/onboarding/yandex-connect')
    } else if (opt.destination === 'tariff') {
      bookmarkStep('tariff', opt.branch)
      navigate('/onboarding/tariff')
    } else if (opt.destination === 'showcase-consent') {
      bookmarkStep('showcase-consent', opt.branch)
      navigate('/onboarding/showcase-consent')
    } else {
      bookmarkStep(null, opt.branch)
      navigate('/dashboard?welcome=1')
    }
  }

  function handleSkip() {
    bookmarkStep(null, 'quick')
    navigate('/dashboard?welcome=1')
  }

  return (
    <OnboardingShell
      step="branch"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate(-1)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button className="foot-skip" onClick={handleSkip}>
            Решу потом, к тарифу
          </button>
        </>
      }
    >
      <p className="stage-eyebrow">
        <span className="stage-eyebrow-pill">Развилка</span>
        Цель использования
      </p>
      <h1 className="stage-title">
        С чего <em>начнём</em>?
      </h1>
      <p className="stage-sub">
        Выберите <b>что хотите сделать сразу</b> — дальше пройдём настройку именно под это. Остальные инструменты будут доступны в кабинете в любой момент.
      </p>

      <div className="first-grid">
        {options.map((opt, i) => (
          <button
            key={i}
            className={`first-card ${opt.featured ? 'featured' : ''} ${opt.minor ? 'minor' : ''}`}
            onClick={() => handlePick(opt)}
          >
            <div className={`first-icon ${opt.minor ? 'minor' : ''}`}>{opt.icon}</div>
            <div className="first-name">
              {opt.name}
              {opt.badge && <span className="first-badge">{opt.badge}</span>}
            </div>
            <div className="first-desc">{opt.desc}</div>
            <div className="first-time">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              {opt.time}
            </div>
          </button>
        ))}
      </div>

      <style>{`
        .first-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          max-width: 1100px;
        }
        @media (max-width: 900px) {
          .first-grid { grid-template-columns: repeat(2, 1fr); }
        }
        .first-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          padding: 24px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 12px;
          text-align: left;
          font-family: inherit;
          transition: border-color 200ms, box-shadow 200ms, transform 200ms;
          position: relative;
          overflow: hidden;
        }
        .first-card:hover {
          border-color: rgba(0,128,255,0.4);
          box-shadow: 0 8px 24px -8px rgba(38,40,45,0.1);
          transform: translateY(-2px);
        }
        .first-card.featured {
          border-color: var(--tg-blue, #0080ff);
          background:
            radial-gradient(circle at 100% 0%, var(--tg-blue-bg, #ebf2ff) 0%, transparent 70%),
            var(--tg-surface, #fff);
        }
        .first-card.featured::before {
          content: "Рекомендуем";
          position: absolute;
          top: 12px; right: 12px;
          padding: 3px 10px;
          background: var(--tg-blue, #0080ff);
          color: white;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .first-card.minor { opacity: 0.85; }
        .first-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0066cc);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .first-icon.minor {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-mute, #8f9eb0);
        }
        .first-icon svg { width: 24px; height: 24px; }
        .first-name {
          font-weight: 800;
          font-size: 17px;
          letter-spacing: -0.01em;
          color: var(--tg-ink, #111);
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }
        .first-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(0, 168, 96, 0.12);
          color: var(--tg-up, #00a860);
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          vertical-align: middle;
        }
        .first-desc {
          font-size: 13px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
          flex: 1;
        }
        .first-desc b { font-weight: 700; }
        .first-time {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding-top: 10px;
          border-top: 1px dashed var(--tg-line-2, #f0f0f4);
        }
        .first-time svg { width: 12px; height: 12px; }
      `}</style>
    </OnboardingShell>
  )
}
